import { useCallback, useEffect, useRef, useState } from "react";
import useDetails from "../../hooks/useDetails";
import api from "../../api/api";
import {
  LuBell,
  LuSearch,
  LuDownload,
  LuBookOpen,
  LuLibrary,
  LuLoader,
  LuInbox,
  LuAlertTriangle,
  LuLock,
} from "react-icons/lu";

const FORMAT = {
  EBOOK: "EBOOK",
  PHYSICAL: "PHYSICAL",
};

const FILTERS = [
  { label: "All", value: "" },
  { label: "E-Books", value: FORMAT.EBOOK },
  { label: "Physical", value: FORMAT.PHYSICAL },
];

// download_url/file are sometimes present-but-empty even on EBOOK records,
// so "is an ebook" and "is downloadable" have to be checked separately.
const getDownloadHref = (book) => book.download_url || book.file || null;
const isDownloadable = (book) =>
  book.format === FORMAT.EBOOK && Boolean(getDownloadHref(book));

const initials = (title = "") =>
  title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const SkeletonCard = () => (
  <div className="bg-[#FBFBFB] border border-[#ECECEC80] rounded-xl p-3 space-y-3 animate-pulse">
    <div className="aspect-[3/4] rounded-lg bg-gray-200" />
    <div className="h-4 w-3/4 rounded bg-gray-200" />
    <div className="h-3 w-1/2 rounded bg-gray-200" />
    <div className="h-7 w-full rounded-lg bg-gray-200" />
  </div>
);

const FormatBadge = ({ format }) => {
  const isEbook = format === FORMAT.EBOOK;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full ${
        isEbook
          ? "bg-indigo-50 text-indigo-600"
          : "bg-amber-50 text-amber-700"
      }`}
    >
      {isEbook ? <LuBookOpen className="size-3" /> : <LuLibrary className="size-3" />}
      {isEbook ? "E-Book" : "Physical"}
    </span>
  );
};

const BookCover = ({ book }) => {
  if (book.cover_image) {
    return (
      <img
        src={book.cover_image}
        alt={book.title}
        className="w-full aspect-[3/4] object-cover rounded-lg bg-gray-100"
        loading="lazy"
      />
    );
  }
  const isEbook = book.format === FORMAT.EBOOK;
  return (
    <div
      className={`w-full aspect-[3/4] rounded-lg flex items-center justify-center text-xl font-bold ${
        isEbook ? "bg-indigo-50 text-indigo-400" : "bg-amber-50 text-amber-500"
      }`}
    >
      {initials(book.title) || (isEbook ? <LuBookOpen /> : <LuLibrary />)}
    </div>
  );
};

const BookAction = ({ book }) => {
  if (book.format === FORMAT.EBOOK) {
    if (isDownloadable(book)) {
      return (
        <a
          href={getDownloadHref(book)}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-sm font-medium bg-brand-darkGray text-white hover:opacity-90 transition-opacity"
        >
          <LuDownload className="size-4" />
          Download
        </a>
      );
    }
    return (
      <div className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-400">
        <LuLock className="size-3.5" />
        Not available for download
      </div>
    );
  }

  const available = book.copies_available > 0;
  return (
    <div className="flex items-center justify-between w-full py-2 px-1 text-sm">
      <span className="flex items-center gap-1.5 font-medium text-gray-600">
        <span
          className={`size-2 rounded-full ${
            available ? "bg-emerald-500" : "bg-rose-400"
          }`}
        />
        {available ? "In library" : "Currently unavailable"}
      </span>
      <span className="text-xs text-gray-400">
        {book.copies_available}/{book.copies_total} copies
      </span>
    </div>
  );
};

const BookCard = ({ book }) => (
  <div className="bg-[#FBFBFB] border border-[#ECECEC80] rounded-xl p-3 space-y-3 flex flex-col">
    <div className="relative">
      <BookCover book={book} />
      <div className="absolute top-2 left-2">
        <FormatBadge format={book.format} />
      </div>
    </div>

    <div className="space-y-0.5 flex-1">
      <h3 className="text-sm font-semibold text-brand-black leading-snug line-clamp-2">
        {book.title}
      </h3>
      <p className="text-xs text-gray-500 truncate">{book.author}</p>
    </div>

    <BookAction book={book} />
  </div>
);

const EmptyState = ({ hasQuery }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
    <LuInbox className="size-8 text-gray-300" />
    <p className="text-sm font-medium text-gray-500">
      {hasQuery ? "No books match your search" : "No books in the library yet"}
    </p>
    {hasQuery && (
      <p className="text-xs text-gray-400">Try a different title, author, or filter</p>
    )}
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
    <LuAlertTriangle className="size-8 text-rose-400" />
    <p className="text-sm font-medium text-gray-500">Couldn't load the library</p>
    <button
      onClick={onRetry}
      className="text-sm font-medium text-brand-darkGray underline underline-offset-2"
    >
      Try again
    </button>
  </div>
);

const StatPill = ({ label, value }) => (
  <div className="bg-[#FBFBFB] border border-[#ECECEC80] rounded-lg px-3 py-2">
    <p className="text-lg font-bold text-brand-black leading-none">{value}</p>
    <p className="text-xs text-gray-400 mt-1">{label}</p>
  </div>
);

const Library = () => {
  const { data: details } = useDetails();

  const [library, setLibrary] = useState([]);
  const [stats, setStats] = useState({ total: 0, ebooks: 0, physical: 0 });
  const [status, setStatus] = useState("loading"); // loading | ready | error | loading-more
  const [nextPage, setNextPage] = useState(null);

  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("");
  const debounceRef = useRef(null);

  const getLibrary = useCallback(
    async ({ append = false, url = null } = {}) => {
      setStatus(append ? "loading-more" : "loading");
      try {
        const { data } = url
          ? await api.get(url)
          : await api.get("/library/books/", {
              params: {
                ...(search ? { search } : {}),
                ...(format ? { format } : {}),
              },
            });

        const results = data?.results ?? [];
        setLibrary((prev) => (append ? [...prev, ...results] : results));
        setNextPage(data?.next ?? null);

        if (!append) {
          setStats({
            total: data?.count ?? results.length,
            ebooks: results.filter((b) => b.format === FORMAT.EBOOK).length,
            physical: results.filter((b) => b.format === FORMAT.PHYSICAL).length,
          });
        }
        setStatus("ready");
      } catch (error) {
        console.log(error);
        setStatus("error");
      }
    },
    [search, format]
  );

  // Debounced refetch when search or format filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getLibrary();
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, format]);

  const isInitialLoading = status === "loading" && library.length === 0;
  const hasQuery = Boolean(search || format);

  return (
    <div>
      {details && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-brand-darkGray">Library</h1>
              <p className="text-sm text-gray-400">
                Explore e-books and physical titles in your school library.
              </p>
            </div>

            <div className="hidden lg:flex items-center gap-2.5">
              <div className="relative w-[330px]">
                <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-4" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title or author"
                  className="w-full py-3 pl-9 pr-3 text-sm rounded-lg bg-white"
                />
              </div>
              <div className="size-12 bg-white rounded-lg flex items-center justify-center">
                <LuBell className="text-gray-400 size-4" />
              </div>
            </div>
          </div>

          {/* Mobile search */}
          <div className="relative lg:hidden">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-4" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or author"
              className="w-full py-3 pl-9 pr-3 text-sm rounded-lg bg-white"
            />
          </div>

          <div className="bg-white rounded-xl card-body">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h5 className="text-lg font-semibold text-brand-darkGray">
                  &#x2022; Library Resources
                </h5>

                <div className="flex items-center gap-1 bg-[#FBFBFB] border border-[#ECECEC80] rounded-lg p-1 w-fit">
                  {FILTERS.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setFormat(f.value)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                        format === f.value
                          ? "bg-brand-darkGray text-white"
                          : "text-gray-500 hover:text-brand-darkGray"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {status === "ready" && library.length > 0 && (
                <div className="flex gap-3">
                  <StatPill label="Total titles" value={stats.total} />
                  <StatPill label="E-Books" value={stats.ebooks} />
                  <StatPill label="Physical" value={stats.physical} />
                </div>
              )}

              {status === "error" && <ErrorState onRetry={() => getLibrary()} />}

              {isInitialLoading && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              )}

              {status !== "error" && !isInitialLoading && library.length === 0 && (
                <EmptyState hasQuery={hasQuery} />
              )}

              {!isInitialLoading && library.length > 0 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {library.map((book) => (
                      <BookCard key={book.id} book={book} />
                    ))}
                  </div>

                  {nextPage && (
                    <div className="flex justify-center pt-2">
                      <button
                        onClick={() => getLibrary({ append: true, url: nextPage })}
                        disabled={status === "loading-more"}
                        className="flex items-center gap-2 text-sm font-medium text-brand-darkGray border border-[#ECECEC80] rounded-lg px-4 py-2 hover:bg-[#FBFBFB] disabled:opacity-50"
                      >
                        {status === "loading-more" && (
                          <LuLoader className="size-4 animate-spin" />
                        )}
                        Load more
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;